import React, { useCallback, useEffect, useRef } from 'react';
import { DownOutlined } from '@ant-design/icons';
import { Popover } from 'antd';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

export interface IActiveFilterChip {
  id: string;
  label: string;
  value: string;
  /** This category's selector, shown floating over the page while this chip is the open one. */
  selector?: React.ReactNode;
}

export interface IActiveFilterBarProps {
  /** One chip per filter category, in panel order — including the ones still on their default. */
  chips: IActiveFilterChip[];
  /** Which chips are off their default, so the bar can mark them without re-deriving the defaults. */
  activeIds?: string[];
  /** The single open selector, or `null`. One slot, so a second chip cannot open beside the first. */
  openId?: string | null;
  onToggleChip?: (id: string) => void;
  onCloseChip?: () => void;
  onClearAll: () => void;
  className?: string;
}

// `Component 2` in Figma node 589-33313, read off the scene graph rather than pixels
// (design/filter-spec-589-33313-measured-2026-08-12.md § 1). The chips are rounded rectangles, not
// pills: at 34px tall a pill would be r=17. A pixel pass read the arc as 5px and this briefly
// shipped at that; `corner radius/9` is the declared value. PLA-8815.
const CHIP_HEIGHT = '34px';
const CHIP_RADIUS = '9px';

// The frame is 1335x76 for two rows and 34 + 8 + 34 = 76, so the bar carries no vertical padding of
// its own — the rows are the whole of it. The negative margins below cancel the trailing gap the
// per-item margins add, so the row width and the two-row height stay exactly the measured 76px.
const Bar = styled.div`
  display: flex;
  flex-direction: column;

  /* The bar's whole box is its rows: whatever seam it sits in belongs to the container placing it
     (today PageHeader's filter slot), never to the bar. Doubled so a parent's single-class sibling
     rhythm cannot reintroduce spacing here by winning on source order. */
  && {
    margin-top: 0;
  }
`;

const Chips = styled.ul`
  display: flex;
  flex-wrap: wrap;
  margin: 0 calc(-1 * ${tokens.space['8']}) calc(-1 * ${tokens.space['8']}) 0;
  padding: 0;
  list-style: none;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * {
    margin: 0 ${tokens.space['8']} ${tokens.space['8']} 0;
  }
`;

const Chip = styled.li`
  /* The selector portals into this host (see getPopupContainer), which has to be positioned for the
     overlay to anchor and has to keep the --is-* token scope a body portal would leave. */
  position: relative;

  /* public/*-theme.css predates antd 4.20 and clamps the overlay to max-width 100% — 100% of this
     chip — which collapsed a card explainer's painted background to a sliver (fixed in a60a3e9f9).
     The selector portals the same way, so it takes the same pin to the viewport. */
  .ant-popover {
    max-width: 100vw;
  }

  .ant-popover-inner {
    border-radius: ${tokens.radius.md};
    background: transparent;
    box-shadow: ${tokens.elevation.overlay};
  }

  .ant-popover-inner-content {
    padding: 0;
  }

  /* Figma draws the card as a plain floating panel with no pointer. antd 4.24 paints the arrow on a
     pseudo-element from --antd-arrow-background-color, which the light theme leaves white and which
     no background declaration on .ant-popover-arrow-content can reach, so it goes rather than being
     recoloured. */
  .ant-popover-arrow {
    display: none;
  }
`;

const ChipButton = styled.button`
  display: flex;
  align-items: center;
  height: ${CHIP_HEIGHT};
  padding: 0 ${tokens.space['12']};
  border: 1px solid ${tokens.color['filter-border']};
  border-radius: ${CHIP_RADIUS};
  background: ${tokens.color['filter-surface']};
  color: ${tokens.color['filter-text']};
  font-size: ${tokens.type['body-size']};
  line-height: ${tokens.type['body-line']};
  cursor: pointer;

  > * + * {
    margin-left: ${tokens.space['8']};
  }

  .anticon {
    font-size: 9px;
    color: ${tokens.color['filter-caret']};
  }

  &[data-active='true'],
  &[aria-expanded='true'] {
    border-color: ${tokens.color['filter-accent']};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.elevation['focus-ring']};
  }
`;

// Both halves are bold in Figma — the label reads dimmer purely through colour, not weight
// (filter-spec § 1, `span.lbl` / `span.val` are both 700).
const ChipLabel = styled.span`
  color: ${tokens.color['filter-text-muted']};
  font-weight: ${tokens.type['card-title-weight']};
`;

// `span.val` carries `Max width 190px` in Figma, so a long selection is expected to clamp rather
// than widen the chip (filter-spec § 1). The longest shipped value, "All permitted demographics",
// sits under it — the clamp is what keeps a future longer one from stretching the row.
const ChipValue = styled.span`
  max-width: 190px;
  overflow: hidden;
  color: ${tokens.color['filter-text']};
  font-weight: ${tokens.type['card-title-weight']};
  white-space: nowrap;
  text-overflow: ellipsis;
`;

// `Clear all filters` shares the last chip row rather than claiming a third one, so `auto` against
// the row's own line. Doubled: it has to outrank `Chips > *`'s margin shorthand, which resets
// margin-left at the same specificity.
const ClearAllItem = styled.li`
  display: flex;
  align-items: center;

  && {
    margin-left: auto;
  }
`;

// Figma inks this at rest in the same muted grey as the chip labels, not in the accent. The brighten
// on hover and on keyboard focus is ours — the design draws no interaction state for it — so it
// stays inside the palette and adds no motion, this panel using none anywhere else.
const ClearAllButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: ${tokens.color['filter-text-muted']};
  font-size: ${tokens.type['card-description-size']};
  font-weight: ${tokens.type['card-title-weight']};
  line-height: ${tokens.type['card-description-line']};
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: ${tokens.color['filter-text']};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.elevation['focus-ring']};
  }
`;

// The filter bar: one trigger per category, each opening its own selector floating over the page.
// Which one is open is a single value owned upstream, so opening a second closes the first without
// the bar coordinating anything. PLA-8815.
const ActiveFilterBar: React.FC<IActiveFilterBarProps> = ({
  chips,
  activeIds,
  openId = null,
  onToggleChip,
  onCloseChip,
  onClearAll,
  className
}) => {
  const { t } = useMultiLanguageTranslation();
  const triggers = useRef<Record<string, HTMLButtonElement | null>>({});

  // The selector portals out of the bar, so a keypress inside it never bubbles here; the listener has
  // to sit on the document for Escape to reach the open selector at all.
  useEffect(() => {
    if (!openId || !onCloseChip) {
      return undefined;
    }

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseChip();
        triggers.current[openId]?.focus();
      }
    };

    document.addEventListener('keydown', closeOnEscape);

    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [onCloseChip, openId]);

  const handleOpenChange = useCallback(
    (chipId: string, open: boolean) => {
      if (open) {
        onToggleChip?.(chipId);

        return;
      }

      onCloseChip?.();
      triggers.current[chipId]?.focus();
    },
    [onCloseChip, onToggleChip]
  );

  if (chips.length === 0) {
    return null;
  }

  return (
    <Bar className={className} data-testid="active-filter-bar">
      <Chips>
        {chips.map(chip => {
          const open = openId === chip.id;
          const trigger = (
            <ChipButton
              type="button"
              ref={node => {
                triggers.current[chip.id] = node;
              }}
              aria-expanded={open}
              data-active={activeIds ? activeIds.includes(chip.id) : false}
              data-testid="active-filter-chip-trigger"
              // With a selector attached, the Popover already turns a press into `onOpenChange`;
              // handling the click here as well would toggle twice and never open.
              onClick={chip.selector ? undefined : () => onToggleChip?.(chip.id)}
            >
              <ChipLabel data-testid="active-filter-chip-label">{chip.label}</ChipLabel>
              <ChipValue data-testid="active-filter-chip-value">{chip.value}</ChipValue>
              <DownOutlined aria-hidden />
            </ChipButton>
          );

          return (
            <Chip key={chip.id} data-testid="active-filter-chip" data-filter={chip.id}>
              {chip.selector ? (
                <Popover
                  content={chip.selector}
                  // `click`, never `focus`: Safari does not fire focus on a button pressed with the
                  // mouse (conventions/components/cross-browser.md § 1).
                  trigger="click"
                  placement="bottomLeft"
                  open={open}
                  destroyTooltipOnHide
                  getPopupContainer={triggerNode => triggerNode.parentElement}
                  onOpenChange={next => handleOpenChange(chip.id, next)}
                >
                  {trigger}
                </Popover>
              ) : (
                trigger
              )}
            </Chip>
          );
        })}
        <ClearAllItem>
          <ClearAllButton type="button" onClick={onClearAll} data-testid="active-filter-clear-all">
            {t(`${TCustomerInsights}.Filters.ClearAll`)}
          </ClearAllButton>
        </ClearAllItem>
      </Chips>
    </Bar>
  );
};

export default ActiveFilterBar;
